import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // Light is the default: the IC look puts a navy CANVAS behind WHITE content
    // surfaces (cards, tables, modals stay light with dark text), matching the
    // customer selector pages. Dark mode remains a real, separate theme.
    //
    // Key is `.canvas`-suffixed to abandon two earlier stored values: the
    // pre-IC `adminTheme`, and `adminTheme.ic`, which a previous build wrote
    // as "dark" when the whole console was briefly dark-themed. Reading either
    // would pin an existing browser to dark and defeat this default.
    localStorage.removeItem("adminTheme");
    localStorage.removeItem("adminTheme.ic");
    const stored = localStorage.getItem("adminTheme.canvas") as Theme | null;
    return stored === "dark" || stored === "light" ? stored : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("adminTheme.canvas", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
